import React from "react";
import { ProviderIcon } from "../../shared/ProviderIcon";
import { PROVIDER_ORDER } from "./providerMap";
import { hasSignalCoverage } from "./strategyShape";
import { TIER_DOT_CLASS, TIER_LABEL, tierOf } from "./tierColors";
import { engineLabel } from "./strategyUtils";
import ExpandableText from "./ExpandableText";

const IMPORTANCE_STYLES = {
	critical: "monitoring__analysis-strategy-importance-badge--critical",
	high: "monitoring__analysis-strategy-importance-badge--high",
	medium: "monitoring__analysis-strategy-importance-badge--medium",
	low: "monitoring__analysis-strategy-importance-badge--low",
};

export function CoverageLegend() {
	return (
		<div className="monitoring__analysis-strategy-legend">
			{[3, 2, 1, 0].map((tier) => (
				<span key={tier} className="monitoring__analysis-strategy-legend-item">
					<span
						className={`monitoring__analysis-strategy-tier-dot ${TIER_DOT_CLASS[tier]}`}
					/>
					{TIER_LABEL[tier]}
				</span>
			))}
		</div>
	);
}

export default function SignalsAndConsensus({ strategy, embedded = false }) {
	const sorted = [...strategy.cross_provider_signals].sort(
		(a, b) => b.coverage_count - a.coverage_count
	);

	const table = (
		<div className="monitoring__analysis-strategy-signals-scroll">
					<table className="monitoring__analysis-strategy-signals-table">
						<thead>
							<tr>
								<th className="monitoring__analysis-strategy-signals-col-signal">
									Signal / Factor
								</th>
								{PROVIDER_ORDER.map((p) => (
									<th
										key={p}
										className="monitoring__analysis-strategy-signals-col-provider text-center"
									>
										<div className="monitoring__analysis-strategy-signals-provider-head">
											<ProviderIcon provider={p} size={14} />
											<span>{engineLabel(p)}</span>
										</div>
									</th>
								))}
								<th className="monitoring__analysis-strategy-signals-col-why">
									Why it matters
								</th>
							</tr>
						</thead>
						<tbody>
							{sorted.map((s) => {
								const tier = tierOf(s.coverage_count);
								return (
									<tr key={s.id}>
										<td>
											<p className="fw-semibold small mb-1">{s.name}</p>
											<div className="d-flex flex-wrap align-items-center gap-1">
												<span className="monitoring__analysis-strategy-category-badge">
													{s.category}
												</span>
												<span
													className={`monitoring__analysis-strategy-importance-badge ${
														IMPORTANCE_STYLES[s.importance] ??
														IMPORTANCE_STYLES.low
													}`}
												>
													{s.importance}
												</span>
											</div>
										</td>
										{PROVIDER_ORDER.map((p) => (
											<td
												key={p}
												className="monitoring__analysis-strategy-signals-col-provider text-center"
											>
												<span
													className={`monitoring__analysis-strategy-tier-dot ${
														hasSignalCoverage(s, p)
															? TIER_DOT_CLASS[tier]
															: TIER_DOT_CLASS[0]
													}`}
												/>
											</td>
										))}
										<td className="monitoring__analysis-strategy-signals-col-why">
											<ExpandableText text={s.description} />
										</td>
									</tr>
								);
							})}
							{sorted.length === 0 && (
								<tr>
									<td colSpan={5} className="text-center text-muted small py-3">
										No signals available.
									</td>
								</tr>
							)}
						</tbody>
					</table>
		</div>
	);

	if (embedded) {
		return (
			<>
				<p className="small text-muted mb-2">
					Attributes competitors are winning on, ranked by how many providers surfaced
					them.
				</p>
				<div className="d-flex justify-content-end mb-2">
					<CoverageLegend />
				</div>
				{table}
			</>
		);
	}

	return (
		<div className="monitoring__analysis-strategy-card">
			<div className="monitoring__analysis-strategy-card-head">
				<div>
					<h4 className="monitoring__analysis-strategy-section-heading mb-1">
						Cross-Provider Winning Signals ({sorted.length})
					</h4>
					<p className="small text-muted mb-0">
						Attributes competitors are winning on, ranked by how many providers surfaced
						them.
					</p>
				</div>
				<CoverageLegend />
			</div>
			<div className="monitoring__analysis-strategy-card-body pt-0">{table}</div>
		</div>
	);
}
